import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, BedDouble, Trash2, Building2, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emptyProp = { name: "", city: "", country: "Singapore", tagline: "", description: "", hero_image: "", gallery: [], amenities: [] };
const emptyRoom = { name: "", size: "", beds: "", occupancy: 2, price_from: "", description: "", image: "", perks: [] };
const cls = "w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2.5 font-sans text-sm text-[#F1F5F9] focus:border-gold focus:outline-none";

export default function AdminProperties() {
  const [properties, setProperties] = useState([]);
  const [propOpen, setPropOpen] = useState(false);
  const [roomOpen, setRoomOpen] = useState(null); // property id
  const [prop, setProp] = useState(emptyProp);
  const [room, setRoom] = useState(emptyRoom);
  const [editingProp, setEditingProp] = useState(null);
  const [editingRoomId, setEditingRoomId] = useState(null);

  const load = () => api.get("/admin/properties").then((r) => setProperties(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const newProperty = () => { setProp(emptyProp); setEditingProp(null); setPropOpen(true); };
  const startEditProp = (p) => {
    setProp({ ...p, gallery: (p.gallery || []).join(", "), amenities: (p.amenities || []).join(", ") });
    setEditingProp(p.id); setPropOpen(true);
  };
  const delProp = async (id) => {
    if (!window.confirm("Delete this property and all its rooms?")) return;
    await api.delete(`/admin/properties/${id}`); toast.success("Property deleted"); load();
  };

  const saveProp = async () => {
    const body = {
      ...prop,
      amenities: typeof prop.amenities === "string" ? prop.amenities.split(",").map((s) => s.trim()).filter(Boolean) : prop.amenities,
      gallery: typeof prop.gallery === "string" ? prop.gallery.split(",").map((s) => s.trim()).filter(Boolean) : prop.gallery,
    };
    try {
      if (editingProp) await api.put(`/admin/properties/${editingProp}`, body);
      else await api.post("/admin/properties", body);
      toast.success(editingProp ? "Property updated" : "Property added");
      setPropOpen(false); setProp(emptyProp); setEditingProp(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const newRoom = (pid) => { setRoom(emptyRoom); setEditingRoomId(null); setRoomOpen(pid); };
  const startEditRoom = (pid, r) => {
    setRoom({ ...r, perks: (r.perks || []).join(", ") });
    setEditingRoomId(r.id); setRoomOpen(pid);
  };

  const saveRoom = async () => {
    const body = {
      ...room, occupancy: Number(room.occupancy),
      perks: typeof room.perks === "string" ? room.perks.split(",").map((s) => s.trim()).filter(Boolean) : room.perks,
    };
    try {
      if (editingRoomId) await api.put(`/admin/rooms/${editingRoomId}`, body);
      else await api.post(`/admin/properties/${roomOpen}/rooms`, body);
      toast.success(editingRoomId ? "Room updated" : "Room added");
      setRoomOpen(null); setRoom(emptyRoom); setEditingRoomId(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const delRoom = async (id) => { await api.delete(`/admin/rooms/${id}`); toast.success("Room removed"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow">Inventory</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight">Properties & Rooms</h1>
        </div>
        <button onClick={newProperty} data-testid="new-property-btn" className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">
          <Plus size={15} /> Add Property
        </button>
      </div>

      <div className="mt-8 space-y-6">
        {properties.map((p) => (
          <div key={p.id} data-testid={`admin-property-${p.id}`} className="overflow-hidden rounded-lg border border-white/5 bg-[#121722]">
            <div className="flex items-center gap-4 p-5">
              {p.hero_image ? <img src={p.hero_image} alt="" className="h-16 w-24 rounded-md object-cover" /> : <div className="flex h-16 w-24 items-center justify-center rounded-md bg-white/5"><Building2 className="text-[#94A3B8]" /></div>}
              <div className="flex-1">
                <h3 className="font-serif text-xl">{p.name}</h3>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{p.city}, {p.country} · {p.rooms?.length || 0} room type(s)</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEditProp(p)} data-testid={`edit-property-${p.id}`} className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-2 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold">
                  <Pencil size={13} /> Edit
                </button>
                <button onClick={() => delProp(p.id)} data-testid={`delete-property-${p.id}`} className="rounded-full border border-white/10 p-2 text-[#94A3B8] hover:border-rose-500 hover:text-rose-400">
                  <Trash2 size={14} />
                </button>
                <button onClick={() => newRoom(p.id)} data-testid={`add-room-${p.id}`} className="flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-gold hover:bg-gold hover:text-[#0A0D14]">
                  <Plus size={14} /> Add Room
                </button>
              </div>
            </div>
            {p.rooms?.length > 0 && (
              <div className="border-t border-white/5 px-5 py-3">
                <div className="grid gap-2 sm:grid-cols-3">
                  {p.rooms.map((r) => (
                    <div key={r.id} className="flex items-center justify-between rounded-md border border-white/5 bg-[#0A0D14] px-3 py-2">
                      <span className="flex items-center gap-2 font-sans text-sm"><BedDouble size={14} className="text-gold" />{r.name} <span className="text-xs text-[#94A3B8]">{r.price_from}</span></span>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => startEditRoom(p.id, r)} data-testid={`edit-room-${r.id}`} className="text-[#94A3B8] hover:text-gold"><Pencil size={12} /></button>
                        <button onClick={() => delRoom(r.id)} data-testid={`delete-room-${r.id}`} className="text-[#94A3B8] hover:text-rose-400"><Trash2 size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Property dialog */}
      <Dialog open={propOpen} onOpenChange={setPropOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editingProp ? "Edit Property" : "Add Property"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input placeholder="Name" value={prop.name} onChange={(e) => setProp({ ...prop, name: e.target.value })} className={cls} data-testid="prop-name" />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="City" value={prop.city} onChange={(e) => setProp({ ...prop, city: e.target.value })} className={cls} data-testid="prop-city" />
              <input placeholder="Country" value={prop.country} onChange={(e) => setProp({ ...prop, country: e.target.value })} className={cls} />
            </div>
            <input placeholder="Tagline" value={prop.tagline} onChange={(e) => setProp({ ...prop, tagline: e.target.value })} className={cls} data-testid="prop-tagline" />
            <textarea placeholder="Description" value={prop.description} onChange={(e) => setProp({ ...prop, description: e.target.value })} rows={3} className={cls} />
            <input placeholder="Hero image URL" value={prop.hero_image} onChange={(e) => setProp({ ...prop, hero_image: e.target.value })} className={cls} data-testid="prop-hero" />
            <input placeholder="Gallery image URLs (comma separated)" value={prop.gallery} onChange={(e) => setProp({ ...prop, gallery: e.target.value })} className={cls} />
            <input placeholder="Amenities (comma separated)" value={prop.amenities} onChange={(e) => setProp({ ...prop, amenities: e.target.value })} className={cls} />
            <button onClick={saveProp} data-testid="prop-save" className="w-full rounded-full bg-gold px-6 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">{editingProp ? "Update Property" : "Save Property"}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room dialog */}
      <Dialog open={!!roomOpen} onOpenChange={(o) => !o && setRoomOpen(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editingRoomId ? "Edit Room" : "Add Room"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input placeholder="Room name (e.g. Deluxe Room)" value={room.name} onChange={(e) => setRoom({ ...room, name: e.target.value })} className={cls} data-testid="room-name" />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Size (e.g. 42 m²)" value={room.size} onChange={(e) => setRoom({ ...room, size: e.target.value })} className={cls} />
              <input placeholder="Beds (e.g. 1 King)" value={room.beds} onChange={(e) => setRoom({ ...room, beds: e.target.value })} className={cls} />
              <input type="number" placeholder="Occupancy" value={room.occupancy} onChange={(e) => setRoom({ ...room, occupancy: e.target.value })} className={cls} />
              <input placeholder="Price from (e.g. S$420)" value={room.price_from} onChange={(e) => setRoom({ ...room, price_from: e.target.value })} className={cls} data-testid="room-price" />
            </div>
            <input placeholder="Image URL" value={room.image} onChange={(e) => setRoom({ ...room, image: e.target.value })} className={cls} data-testid="room-image" />
            <textarea placeholder="Description" value={room.description} onChange={(e) => setRoom({ ...room, description: e.target.value })} rows={2} className={cls} />
            <input placeholder="Perks (comma separated)" value={room.perks} onChange={(e) => setRoom({ ...room, perks: e.target.value })} className={cls} />
            <button onClick={saveRoom} data-testid="room-save" className="w-full rounded-full bg-gold px-6 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">{editingRoomId ? "Update Room" : "Save Room"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
